import React, { useState } from "react";
import { Share2, Copy, Check, MessageCircle, Mail, Send } from "lucide-react";
import { toast } from "sonner";

// Brand-colored channel buttons. We use SVG paths from simple-icons for proper brand
// logos (lucide-react has Twitter but not WhatsApp/Telegram brand marks).
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.85 9.85 0 0 0 1.519 5.263l-1.001 3.654 3.971-1.041zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.711.307 1.265.49 1.697.628.713.227 1.362.195 1.875.118.572-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/></svg>
  );
}
function TelegramIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.473-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
  );
}
function FacebookIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/></svg>
  );
}
function XIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  );
}

export default function ShareListingBlock({ listing }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = listing?.title || "Annuncio su SAN MARINO MARKET";
  const price = listing?.price ? `€${Number(listing.price).toLocaleString("it-IT")}` : "";
  const text = `${title}${price ? ` · ${price}` : ""} su SAN MARINO MARKET`;
  const encoded = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: WhatsAppIcon,
      href: `https://wa.me/?text=${encoded}%20${encodedUrl}`,
      className: "bg-[#25D366] hover:bg-[#1ebe56]",
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: FacebookIcon,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encoded}`,
      className: "bg-[#1877F2] hover:bg-[#1465d8]",
    },
    {
      key: "telegram",
      label: "Telegram",
      Icon: TelegramIcon,
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encoded}`,
      className: "bg-[#229ED9] hover:bg-[#1c87b8]",
    },
    {
      key: "twitter",
      label: "X / Twitter",
      Icon: XIcon,
      href: `https://twitter.com/intent/tweet?text=${encoded}&url=${encodedUrl}`,
      className: "bg-black hover:bg-slate-800",
    },
    {
      key: "email",
      label: "Email",
      Icon: ({ className }) => <Mail className={className} />,
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encoded}%0A%0A${encodedUrl}`,
      className: "bg-slate-600 hover:bg-slate-700",
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        // user cancelled — silent
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="share-listing-block">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-orange-100 text-[#e86a24] flex items-center justify-center">
            <Share2 className="w-4 h-4" />
          </div>
          <h3 className="font-black text-[#1d3d3a] text-sm uppercase tracking-wide">Condividi annuncio</h3>
        </div>
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            onClick={nativeShare}
            className="text-xs font-bold text-[#e86a24] hover:underline"
            data-testid="share-native"
          >
            Altri…
          </button>
        )}
      </div>

      <div className="grid grid-cols-5 gap-2">
        {channels.map((c) => (
          <a
            key={c.key}
            href={c.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`group flex flex-col items-center gap-1 rounded-xl text-white py-3 transition-all hover:-translate-y-0.5 ${c.className}`}
            data-testid={`share-${c.key}`}
            aria-label={`Condividi su ${c.label}`}
          >
            <c.Icon className="w-5 h-5" />
            <span className="text-[10px] font-bold leading-tight">{c.label}</span>
          </a>
        ))}
      </div>

      <button
        onClick={copyLink}
        className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-slate-200 hover:border-[#e86a24] hover:text-[#e86a24] py-2.5 text-sm font-bold text-slate-700 transition-colors"
        data-testid="share-copy-link"
      >
        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        {copied ? "Link copiato!" : "Copia link"}
      </button>

      <p className="text-[10px] text-slate-400 text-center mt-2">
        💡 Suggerimento: per Instagram Story salva il link e incollalo come sticker.
      </p>
    </div>
  );
}
